import type { FeatureAccessMap } from '@riverpark/types'
import { api } from './api'

export interface AuthUser {
  id: string
  name: string
  email: string | null
  phone: string
  role: 'admin' | 'committee' | 'estate_manager' | 'member'
  status: 'active' | 'inactive'
  requiresOnboarding: boolean
  featureAccess: FeatureAccessMap
}

export async function getMe(): Promise<AuthUser | null> {
  try {
    const { user } = await api.get<{ user: AuthUser }>('/auth/me')
    return user
  } catch {
    return null
  }
}

export async function login(identifier: string, password: string, rememberMe = false) {
  return api.post<{ user: AuthUser }>('/auth/login', { identifier, password, rememberMe })
}

export async function logout() {
  return api.post('/auth/logout', {})
}

export async function completeOnboarding(
  name: string,
  email: string,
  phone: string,
  password: string,
  confirmPassword: string,
) {
  return api.post<{ user: AuthUser }>('/auth/complete-onboarding', {
    name,
    email,
    phone,
    password,
    confirmPassword,
  })
}

export async function forgotPassword(identifier: string) {
  return api.post<{
    message: string
    resetUrl?: string
    devOtp?: string
    smsError?: string
    smsDelivery?: 'sent' | 'failed' | 'skipped' | 'not_sent' | 'logged_only'
    atSandbox?: boolean
  }>('/auth/forgot-password', { identifier })
}

export async function verifyPasswordOtp(identifier: string, code: string) {
  return api.post<{
    resetToken: string
    resetUrl: string
    expiresAt: number
    devResetUrl?: string
  }>('/auth/verify-password-otp', { identifier, code })
}

export async function resetPassword(token: string, password: string, confirmPassword: string) {
  return api.post<{ ok: true }>('/auth/reset-password', { token, password, confirmPassword })
}

export interface ResetPasswordPreview {
  email: string
  expiresAt: number
}

export async function getResetPasswordPreview(token: string) {
  return api.get<{ data: ResetPasswordPreview }>(
    `/auth/reset-password?token=${encodeURIComponent(token)}`,
  )
}

export async function acceptInvite(
  token: string,
  name: string,
  phone: string,
  password: string,
  confirmPassword: string,
) {
  return api.post<{ user: AuthUser }>('/auth/accept-invite', {
    token,
    name,
    phone,
    password,
    confirmPassword,
  })
}

export interface InvitePreview {
  email: string
  name: string
  phone: string
  role: 'admin' | 'committee' | 'estate_manager' | 'member'
  expiresAt: number
}

export async function getInvitePreview(token: string) {
  return api.get<{ data: InvitePreview }>(`/auth/invite?token=${encodeURIComponent(token)}`)
}

export function homePathForRole(role: AuthUser['role']): string {
  // Every staff role now lands on its own dashboard — open requests, arrears, and (for
  // estate_manager) unmatched payments up front, plus whatever needs their attention.
  if (role === 'admin') return '/admin/dashboard'
  if (role === 'committee') return '/committee/dashboard'
  if (role === 'estate_manager') return '/operations/dashboard'
  if (role === 'member') return '/dashboard'
  return '/profile'
}

/** Each role's primary nav section (matches nav-config.ts), not the broader set of routes
 * ProtectedRoute happens to also allow them into (e.g. committee/estate_manager can also reach
 * the member portal to manage their own plot, but their notifications should route to their own
 * section). */
export function sectionBasePathForRole(role: AuthUser['role']): string {
  if (role === 'admin') return '/admin'
  if (role === 'committee') return '/committee'
  if (role === 'estate_manager') return '/operations'
  return ''
}

export function postAuthPath(user: AuthUser): string {
  if (user.requiresOnboarding) return '/complete-profile'
  return homePathForRole(user.role)
}

export function formatKES(amount: number): string {
  return `KES ${new Intl.NumberFormat('en-KE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)}`
}
